import { useCallback, useEffect, useMemo, useState } from "react";
import { GetItem, ListItemCategory, GetSubItemCategory } from "../utilities/WebCommunicator";
import { useFormik } from "formik";
import { ItemDetails } from "./ItemDetail";
import { MinusOutlined, PlusOutlined, ShoppingCartOutlined, SearchOutlined } from '@ant-design/icons';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import { Button, Badge } from "antd";
import CryptoJS from "crypto-js";
import { toast } from "react-toastify";

const SECRET_KEY = "d8052f9e09a17e6907629e76bbd261cc";

const decrypt = (encryptedText) => {
    try {
        const bytes = CryptoJS.AES.decrypt(encryptedText, SECRET_KEY);
        return bytes.toString(CryptoJS.enc.Utf8);
    } catch {
        return "";
    }
};

const Orders = () => {
    const [modalShow, setModalShow] = useState(false);
    const [items, setItems] = useState([]);
    const [itemCategories, setItemCategories] = useState([]);
    const [subItemCategories, setSubItemCategories] = useState([]);
    const [activeCategory, setActiveCategory] = useState(null);
    const [activeSubCategory, setActiveSubCategory] = useState(null);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [customError, setCustomError] = useState("");

    const [restaurantId, setRestaurantId] = useState(null);
    const [restaurantName, setRestaurantName] = useState(null);
    const [restaurantDescription, setRestaurantDescription] = useState(null);
    const [subscriptionId, setSubscriptionId] = useState(null);
    const [financialYear, setFinancialYear] = useState(null);

    // Decrypt token from URL hash
    useEffect(() => {
        const hash = window.location.hash?.substring(1);
        const encryptedToken = hash ? decodeURIComponent(hash) : null;
        if (!encryptedToken) {
            toast.error("Invalid menu link. Please scan the QR code again.");
            return;
        }
        try {
            const parsed = JSON.parse(decrypt(encryptedToken));
            setRestaurantId(parsed.restaurantId);
            setRestaurantName(parsed.restaurantName);
            setRestaurantDescription(parsed.restaurantDescription || "");
            setSubscriptionId(parsed.subscriptionId);
            setFinancialYear(parsed.financialYear);
        } catch {
            toast.error("Unable to read menu information. Please scan the QR code again.");
        }
    }, []);

    const authOpts = useMemo(() => ({ subscriptionId, financialYear }), [subscriptionId, financialYear]);

    const formik = useFormik({
        initialValues: {
            id: 0, isActive: true, restaurantId: 1,
            serviceType: "Home Delivery", onlineOrderSourceId: 1, onlineOrderSource: "Zomato",
            customerName: null, mobileNo: null, email: null, address: "Moradabad",
            tableId: null, OrderTakenById: 1, noOfPax: 2, OrderStatus: 0,
            DelayTime: null, kotType: "NORMAL",
            totalQuantity: 0, totalAmount: 0,
            remarks: "Home Delivery", lineItems: [],
        },
    });

    // Load categories
    useEffect(() => {
        if (!subscriptionId) return;
        (async () => {
            const res = await ListItemCategory(restaurantId, authOpts);
            if (res?.data?.length) {
                setItemCategories(res.data);
            } else {
                toast.info("No menu categories available at the moment.");
            }
        })();
    }, [subscriptionId, authOpts]);

    // Auto-select first category
    useEffect(() => {
        if (itemCategories.length && !activeCategory) {
            handleCategoryClick(itemCategories[0].id);
        }
    }, [itemCategories]);

    const handleCategoryClick = useCallback(async (categoryId) => {
        if (!categoryId) return;
        setActiveCategory(categoryId);
        setActiveSubCategory(null);
        setItems([]);
        try {
            const res = await GetSubItemCategory(categoryId, authOpts);
            setSubItemCategories(res?.data || []);
            if (!res?.data?.length) toast.info("No sub-categories found.");

            const resItem = await GetItem(restaurantId, categoryId, authOpts);
            if (resItem?.status === false) {
                toast.error(resItem.message);
                setItems([]);
            } else {
                setItems(resItem?.data?.length ? resItem.data : []);
                console.log("resItem.data", resItem.data);
                if (!resItem?.data?.length) toast.info("No items available in this category.");
            }
        } catch {
            toast.error("Failed to load sub-categories. Please try again.");
        }
    }, [subscriptionId, authOpts]);

    const handleSubCategoryClick = useCallback(async (categoryId) => {
        if (!categoryId) return;
        setActiveSubCategory(categoryId);
        setLoading(true);
        try {
            const res = await GetItem(restaurantId, categoryId, authOpts);
            if (res?.status === false) {
                toast.error(res.message);
                setItems([]);
            } else {
                setItems(res?.data?.length ? res.data : []);
                if (!res?.data?.length) toast.info("No items available in this category.");
            }
        } catch {
            toast.error("Failed to load items. Please check your connection.");
        } finally {
            setLoading(false);
        }
    }, [restaurantId, authOpts]);

    const addRow = useCallback((itemDetails) => {
        formik.setValues(prev => {
            const lineItems = [...prev.lineItems];
            const idx = lineItems.findIndex(li => li.itemId === itemDetails?.id);
            if (idx > -1) {
                lineItems[idx] = {
                    ...lineItems[idx],
                    quantity: lineItems[idx].quantity + 1,
                    amount: (lineItems[idx].quantity + 1) * lineItems[idx].unitPrice,
                };
            } else {
                lineItems.push({
                    id: 0, index: lineItems.length, isActive: true,
                    itemId: itemDetails?.id, itemName: itemDetails?.itemName,
                    itemUnitId: itemDetails?.itemUnitId, unitPrice: itemDetails?.salePrice,
                    quantity: 1, tax: itemDetails?.tax || 0, amount: itemDetails?.salePrice,
                });
            }
            return { ...prev, lineItems };
        });
    }, []);

    const removeRow = useCallback((index) => {
        formik.setValues(prev => ({
            ...prev,
            lineItems: prev.lineItems.filter((_, i) => i !== index),
        }));
    }, []);

    const handleQuantityChange = useCallback((index, value) => {
        if (index < 0) return;
        formik.setValues(prev => {
            const lineItems = [...prev.lineItems];
            lineItems[index] = { ...lineItems[index], quantity: value, amount: lineItems[index].unitPrice * value };
            return { ...prev, lineItems };
        });
    }, []);

    // Recalculate totals
    useEffect(() => {
        const { totalQuantity, totalAmount } = formik.values.lineItems.reduce(
            (acc, row) => ({ totalQuantity: acc.totalQuantity + row.quantity, totalAmount: acc.totalAmount + row.amount }),
            { totalQuantity: 0, totalAmount: 0 }
        );
        formik.setFieldValue("totalQuantity", totalQuantity);
        formik.setFieldValue("totalAmount", totalAmount);
    }, [formik.values.lineItems]);

    const filteredItems = useMemo(() => {
        if (!searchTerm.trim()) return items;
        const term = searchTerm.toLowerCase();
        return items.filter(item => item?.itemName?.toLowerCase().includes(term));
    }, [items, searchTerm]);
    console.log("filteredItems length", filteredItems.length);
    console.log("filteredItems", filteredItems);

    const handleViewCart = () => {
        if (!formik.values.lineItems.length) {
            setCustomError("Please add at least one item to your cart before proceeding.");
            return;
        }
        setCustomError("");
        setModalShow(true);
    };

    const getItemQty = (itemId) => formik.values.lineItems.find(li => li.itemId === itemId)?.quantity || 0;
    const getItemIndex = (itemId) => formik.values.lineItems.findIndex(li => li.itemId === itemId);

    return (
        <>
            <div style={modalShow ? { pointerEvents: 'none', opacity: 0.1 } : {}}>
                {/* Restaurant Header */}
                <div style={{
                    background: 'linear-gradient(135deg, #d32f2f, #b71c1c)', color: '#fff',
                    padding: '22px 20px 20px', position: 'sticky', top: 0, zIndex: 100,
                    boxShadow: '0 4px 20px rgba(183,28,28,0.35)',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, maxWidth: 600, margin: '0 auto' }}>
                        {/* Restaurant Logo */}
                        <div style={{
                            width: 56, height: 56, borderRadius: '50%', background: 'rgba(255,255,255,0.2)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                            border: '2px solid rgba(255,255,255,0.4)',
                        }}>
                            <RestaurantMenuIcon style={{ fontSize: 30, color: '#fff' }} />
                        </div>
                        {/* Restaurant Info */}
                        <div style={{ textAlign: 'left' }}>
                            <h1 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 800, letterSpacing: 0.5, lineHeight: 1.2 }}>
                                {restaurantName || 'Digital Menu'}
                            </h1>
                            {restaurantDescription && (
                                <p style={{ margin: '3px 0 0', fontSize: 12, opacity: 0.85, fontWeight: 400 }}>
                                    {restaurantDescription}
                                </p>
                            )}
                            <p style={{ margin: '2px 0 0', fontSize: 11, opacity: 0.7, fontWeight: 500, letterSpacing: 0.3 }}>
                                🍽️ Order Online
                            </p>
                        </div>
                    </div>
                </div>

                <div style={{ maxWidth: 960, margin: '0 auto', padding: '0 12px', paddingBottom: 100, background: 'linear-gradient(180deg, #fff5f5 0%, #fafafa 30%, #f5f5f5 100%)', minHeight: '100vh' }}>
                    {/* Categories */}
                    <div style={{ padding: '14px 0 6px', position: 'sticky', top: 100, zIndex: 50, background: 'linear-gradient(180deg, #fff5f5, #fafafa)' }}>
                        <h6 style={{ fontWeight: 700, color: '#333', margin: '0 0 8px 4px', fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.5 }}>Categories</h6>
                        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 6 }} className="hide-scrollbar">
                            {itemCategories.map(cat => (
                                <button key={cat.id} onClick={() => handleCategoryClick(cat.id)}
                                    style={{
                                        flexShrink: 0, padding: '8px 18px', borderRadius: 20, border: 'none', cursor: 'pointer',
                                        fontWeight: 600, fontSize: 13, transition: 'all 0.2s',
                                        background: activeCategory === cat.id ? '#e53935' : '#fff',
                                        color: activeCategory === cat.id ? '#fff' : '#444',
                                        boxShadow: activeCategory === cat.id ? '0 2px 8px rgba(229,57,53,0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
                                    }}>
                                    {cat.itemCategory}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Sub-Categories */}
                    {subItemCategories.length > 0 && (
                        <div style={{ padding: '8px 0 6px' }}>
                            <h6 style={{ fontWeight: 700, color: '#333', margin: '0 0 8px 4px', fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.5 }}>Sub Categories</h6>
                            <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 6 }} className="hide-scrollbar">
                                {subItemCategories.map(sub => (
                                    <button key={sub.id} onClick={() => handleSubCategoryClick(sub.id)}
                                        style={{
                                            flexShrink: 0, padding: '7px 16px', borderRadius: 18,
                                            border: '1.5px solid #1976d2', cursor: 'pointer',
                                            fontWeight: 500, fontSize: 12, transition: 'all 0.2s',
                                            background: activeSubCategory === sub.id ? '#1976d2' : '#fff',
                                            color: activeSubCategory === sub.id ? '#fff' : '#1976d2',
                                        }}>
                                        {sub.itemCategory}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Search */}
                    <div style={{ margin: '10px 0' }}>
                        <div style={{ position: 'relative' }}>
                            <SearchOutlined style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#999', zIndex: 1 }} />
                            <input type="text" placeholder="Search items..." value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                style={{
                                    width: '100%', padding: '10px 14px 10px 38px', borderRadius: 25,
                                    border: '1px solid #ddd', fontSize: 14, outline: 'none', background: '#fff',
                                }}
                            />
                        </div>
                    </div>

                    {/* Items Grid */}
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                            <div className="spinner-border text-danger" role="status" />
                            <p style={{ marginTop: 8 }}>Loading items...</p>
                        </div>
                    ) : filteredItems.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                            <RestaurantMenuIcon style={{ fontSize: 48, color: '#ddd' }} />
                            <p style={{ marginTop: 8 }}>{searchTerm ? 'No items match your search.' : 'No items available. Select a category above.'}</p>
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
                            {filteredItems.map(item => {
                                const qty = getItemQty(item.id);
                                const idx = getItemIndex(item.id);
                                return (
                                    <div key={item.id} style={{
                                        background: '#fff', borderRadius: 12, overflow: 'hidden',
                                         boxShadow: '0 2px 10px rgba(0,0,0,0.08)', border: '1px solid #f0f0f0',
                                        display: 'flex', alignItems: 'center', padding: 12, gap: 12,
                                    }}>
                                        <div style={{ flexShrink: 0, width: 80, height: 80, borderRadius: 10, overflow: 'hidden', background: '#f0f0f0' }}>
                                            {item?.images?.length > 0 ? (
                                                <img src={item.images[0]} alt={item.itemName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            ) : (
                                                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <RestaurantMenuIcon style={{ fontSize: 32, color: '#ccc' }} />
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <p style={{ fontWeight: 600, fontSize: 14, margin: 0, color: '#222', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {item.itemName}
                                            </p>
                                            <p style={{ fontSize: 12, color: '#888', margin: '2px 0 6px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {item.remarks || 'Freshly prepared'}
                                            </p>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <span style={{ fontWeight: 700, fontSize: 15, color: '#2e7d32' }}>₹{item.salePrice}</span>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                    {qty > 0 && (
                                                        <Button type="primary" danger shape="circle" size="small" icon={<MinusOutlined />}
                                                            onClick={() => qty === 1 ? removeRow(idx) : handleQuantityChange(idx, qty - 1)} />
                                                    )}
                                                    {qty > 0 && <span style={{ fontWeight: 700, fontSize: 14, minWidth: 20, textAlign: 'center' }}>{qty}</span>}
                                                    <Button type="primary" shape="circle" size="small" icon={<PlusOutlined />}
                                                        style={{ background: '#2e7d32', borderColor: '#2e7d32' }}
                                                        onClick={() => addRow(item)} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Error Overlay */}
                {customError && (
                    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20 }}>
                        <div style={{ background: '#fff', borderRadius: 12, padding: '24px 20px', maxWidth: 360, width: '100%', textAlign: 'center', position: 'relative', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}>
                            <button onClick={() => setCustomError("")} style={{ position: 'absolute', top: 8, right: 12, background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#999' }}>✕</button>
                            <div style={{ color: '#e53935', fontSize: 36, marginBottom: 8 }}>⚠️</div>
                            <p style={{ fontSize: 15, color: '#333', margin: 0 }}>{customError}</p>
                        </div>
                    </div>
                )}

                {/* Floating Cart Button */}
                {formik.values.totalQuantity > 0 && (
                    <div style={{ position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 100, width: 'calc(100% - 32px)', maxWidth: 420 }}>
                        <button onClick={handleViewCart} style={{
                            width: '100%', padding: '14px 20px', borderRadius: 16, border: 'none', cursor: 'pointer',
                            background: 'linear-gradient(135deg, #e53935, #c62828)', color: '#fff',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            boxShadow: '0 4px 16px rgba(229,57,53,0.4)', fontSize: 15, fontWeight: 600,
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <Badge count={formik.values.totalQuantity} style={{ backgroundColor: '#fff', color: '#e53935', fontWeight: 700 }}>
                                    <ShoppingCartOutlined style={{ fontSize: 24, color: '#fff' }} />
                                </Badge>
                                <span>View Cart</span>
                            </div>
                            <span style={{ fontSize: 16, fontWeight: 700 }}>₹{formik.values.totalAmount}</span>
                        </button>
                    </div>
                )}
            </div>

            <ItemDetails
                value={formik.values}
                authOpts={authOpts}
                subscriptionId={subscriptionId}
                restaurantId={restaurantId}
                restaurantName={restaurantName}
                resetForm={formik.resetForm}
                show={modalShow}
                onHide={() => setModalShow(false)}
                onOrderSuccess={() => {
                    formik.setFieldValue('lineItems', []);
                    setModalShow(false);
                }}
            />

            <style>{`.hide-scrollbar::-webkit-scrollbar { display: none; } .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }`}</style>
        </>
    );
};

export default Orders;
